import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function Assignments() {
  const [assignments, setAssignments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    driver_id: '',
    vehicle_id: '',
    assignment_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assignmentsRes, driversRes, vehiclesRes] = await Promise.all([
        api.get('/assignments'),
        api.get('/drivers'),
        api.get('/vehicles'),
      ]);
      setAssignments(assignmentsRes.data);
      setDrivers(driversRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/assignments', formData);
      toast.success('Assignment created successfully');
      setDialogOpen(false);
      setFormData({
        driver_id: '',
        vehicle_id: '',
        assignment_date: new Date().toISOString().split('T')[0],
      });
      loadData();
    } catch (error) {
      console.error('Failed to create assignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleEndAssignment = async (assignmentId) => {
    try {
      await api.put(`/assignments/${assignmentId}/end`);
      toast.success('Assignment ended successfully');
      loadData();
    } catch (error) {
      console.error('Failed to end assignment:', error);
      toast.error('Failed to end assignment');
    }
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find((d) => d.driver_id === driverId);
    return driver ? driver.full_name : 'Unknown';
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find((v) => v.vehicle_id === vehicleId);
    return vehicle ? `${vehicle.car_number} (${vehicle.manufacturer} ${vehicle.model})` : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shizah-blue"></div>
      </div>
    );
  }

  return (
    <div data-testid="assignments-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Assignments</h1>
          <p className="text-gray-600">Manage vehicle assignments to drivers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-assignment-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
              <Plus size={20} className="mr-2" />
              Create Assignment
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="assignment-dialog">
            <DialogHeader>
              <DialogTitle>Create New Assignment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Driver *</Label>
                <Select
                  value={formData.driver_id}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, driver_id: value }))}
                  data-testid="select-driver"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers
                      .filter((d) => d.status === 'active')
                      .map((driver) => (
                        <SelectItem key={driver.driver_id} value={driver.driver_id}>
                          {driver.driver_id} - {driver.full_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicle *</Label>
                <Select
                  value={formData.vehicle_id}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, vehicle_id: value }))}
                  data-testid="select-vehicle"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles
                      .filter((v) => v.status === 'available')
                      .map((vehicle) => (
                        <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                          {vehicle.car_number} - {vehicle.manufacturer} {vehicle.model}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="assignment_date">Assignment Date *</Label>
                <Input
                  id="assignment_date"
                  type="date"
                  data-testid="input-assignment-date"
                  value={formData.assignment_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, assignment_date: e.target.value }))}
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-assignment-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
                  Create Assignment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="assignments-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Assignment ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Driver
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Assignment Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {assignments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No assignments found. Create your first assignment.
                  </td>
                </tr>
              ) : (
                assignments.map((assignment) => (
                  <tr key={assignment.assignment_id} className="hover:bg-gray-50" data-testid={`assignment-row-${assignment.assignment_id}`}>
                    <td className="px-6 py-4 text-sm font-semibold text-shizah-blue">
                      {assignment.assignment_id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{getDriverName(assignment.driver_id)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getVehicleInfo(assignment.vehicle_id)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(assignment.assignment_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          assignment.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {assignment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {assignment.status === 'active' && (
                        <Button
                          onClick={() => handleEndAssignment(assignment.assignment_id)}
                          data-testid={`end-assignment-${assignment.assignment_id}`}
                          variant="destructive"
                          size="sm"
                        >
                          End Assignment
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}